// src/routes/AppRoutes.tsx
// The application's page routes, rendered independently by every workspace pane.
//
// Extracted from App.tsx so that more than one page can be mounted at a time:
// each tab owns a MemoryRouter and renders this tree against its own history,
// which is why useNavigate, useParams and Link inside a page keep working
// unchanged and stay confined to their tab.

import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
// Layouts
import { SettingsLayout } from '../layouts/SettingsLayout';
import { ProjectSettingsLayout } from '../layouts/ProjectSettingsLayout';

// Auth pages

// App pages
import { InboxPage } from '../pages/InboxPage';
import { DashboardPage } from '../pages/DashboardPage';
import { FormGalleryPage } from '../pages/dev/FormGalleryPage';

// Activity routes — inbox, feed, decisions (docs/ux/activity-ia.md)
import { ActivityPage } from '../pages/activity/ActivityPage';
import { ActivitySettingsPage } from '../pages/activity/ActivitySettingsPage';

import { AdminSettingsPage } from '../pages/admin/AdminSettingsPage';

// Affiliate routes — partner dashboard (offers, campaigns, payouts) + admin area
import { AffiliateDashboardPage } from '../pages/affiliate/AffiliateDashboardPage';
import { OffersPage as AffiliateOffersPage } from '../pages/affiliate/OffersPage';
import { CampaignsPage as AffiliateCampaignsPage } from '../pages/affiliate/CampaignsPage';
import { PayoutsPage as AffiliatePayoutsPage } from '../pages/affiliate/PayoutsPage';
import { AffiliateAdminPage } from '../pages/affiliate/AffiliateAdminPage';

// Analytics routes — dashboards hub, dashboard view (widget grid), saved
// queries, reports + schedules. Replaces the generated CRUD scaffold.
import { DashboardsHubPage } from '../pages/analytics/DashboardsHubPage';
import { DashboardViewPage } from '../pages/analytics/DashboardViewPage';
import { SavedQueriesPage } from '../pages/analytics/SavedQueriesPage';
import { ReportsPage as AnalyticsReportsPage } from '../pages/analytics/ReportsPage';

// Landing routes — pages hub, editor (sections/settings/analytics), template
// gallery, public page render. Replaces the generated CRUD scaffold.
import { PagesListPage as LandingPagesListPage } from '../pages/landing/PagesListPage';
import { PageEditorPage as LandingPageEditorPage } from '../pages/landing/PageEditorPage';
import { TemplatesPage as LandingTemplatesPage } from '../pages/landing/TemplatesPage';

import { ApikeyListPage } from '../pages/apikey/ApikeyListPage';
import { ApikeyPoliciesPage } from '../pages/apikey/ApikeyPoliciesPage';

// Attachment routes — Manager (browse/upload/preview/download/delete,
// ownership + processing rail). See docs/ux/attachment-ia.md.
import { AttachmentManagerPage } from '../pages/attachment/AttachmentManagerPage';

// Audittrail routes — compliance explorer, admin-gated (docs/ux/audittrail-ia.md)
import { AuditExplorerPage } from '../pages/audittrail/AuditExplorerPage';

import { AuthnListPage } from '../pages/authn/AuthnListPage';
import { AuthnDetailPage } from '../pages/authn/AuthnDetailPage';
import { AuthnCreatePage } from '../pages/authn/AuthnCreatePage';
import { AuthnEditPage } from '../pages/authn/AuthnEditPage';
import { AuthnSettingsPage } from '../pages/authn/AuthnSettingsPage';
import { SessionsPage } from '../pages/authn/SessionsPage';
import { SecurityPage } from '../pages/authn/SecurityPage';
import { AuthnAdminPage } from '../pages/authn/AuthnAdminPage';

import { OAuthConsentsPage } from '../pages/authn/OAuthConsentsPage';

import { AuthzInspectorPage } from '../pages/authz/AuthzInspectorPage';
import { BootstrapAdminPage } from '../pages/bootstrap/BootstrapAdminPage';
import { ContextAdminPage } from '../pages/context/ContextAdminPage';
import { SignatureVerifyPage } from '../pages/signature/SignatureVerifyPage';
import { SignaturePartiesPage } from '../pages/signature/SignaturePartiesPage';

import { BillingListPage } from '../pages/billing/BillingListPage';
import { BillingDetailPage } from '../pages/billing/BillingDetailPage';
import { BillingSettingsPage } from '../pages/billing/BillingSettingsPage';
import { BillingDashboardPage } from '../pages/billing/BillingDashboardPage';
import { BillingAccountListPage } from '../pages/billing/BillingAccountListPage';
import { BillingAccountDetailPage } from '../pages/billing/BillingAccountDetailPage';
import { InvoiceListPage } from '../pages/billing/InvoiceListPage';
import { InvoiceCreatePage } from '../pages/billing/InvoiceCreatePage';
import { InvoiceDetailPage } from '../pages/billing/InvoiceDetailPage';
import { InvoiceEditPage } from '../pages/billing/InvoiceEditPage';
import { TransactionListPage } from '../pages/billing/TransactionListPage';
import { TransactionCreatePage } from '../pages/billing/TransactionCreatePage';
import { TransactionDetailPage } from '../pages/billing/TransactionDetailPage';
import { BillingProductListPage } from '../pages/billing/BillingProductListPage';
import { BillingProductCreatePage } from '../pages/billing/BillingProductCreatePage';
import { BillingProductDetailPage } from '../pages/billing/BillingProductDetailPage';
import { BillingProductEditPage } from '../pages/billing/BillingProductEditPage';
import { PriceDetailPage } from '../pages/billing/PriceDetailPage';
import { UsageListPage } from '../pages/billing/UsageListPage';
import { UsageCreatePage } from '../pages/billing/UsageCreatePage';
import { UsageSummaryPage } from '../pages/billing/UsageSummaryPage';
import { JournalEntryListPage } from '../pages/billing/JournalEntryListPage';
import { JournalEntryCreatePage } from '../pages/billing/JournalEntryCreatePage';
import { JournalEntryDetailPage } from '../pages/billing/JournalEntryDetailPage';
import { LedgerPage } from '../pages/billing/LedgerPage';
import { TrialBalancePage } from '../pages/billing/TrialBalancePage';
import { RevenueReportPage } from '../pages/billing/RevenueReportPage';
import { PaymentProvidersPage } from '../pages/payment/PaymentProvidersPage';
import { PaymentSettingsPage } from '../pages/payment/PaymentSettingsPage';
import { PaymentsLedgerPage } from '../pages/payment/PaymentsLedgerPage';
import { FiscalizationDashboardPage } from '../pages/fiscalization/FiscalizationDashboardPage';
import { FiscalPointsPage } from '../pages/fiscalization/FiscalPointsPage';
import { FiscalReceiptsPage } from '../pages/fiscalization/FiscalReceiptsPage';
import { FiscalizationSettingsPage } from '../pages/fiscalization/FiscalizationSettingsPage';
import { FiscalSummaryReportPage } from '../pages/fiscalization/FiscalSummaryReportPage';

import { ResourcesCatalogPage } from '../pages/booking/ResourcesCatalogPage';
import { ResourceDetailPage as BookingResourceDetailPage } from '../pages/booking/ResourceDetailPage';
import { MyBookingsPage } from '../pages/booking/MyBookingsPage';
import { BookingAdminPage } from '../pages/booking/BookingAdminPage';

import { LegalEntityListPage } from '../pages/legalentity/LegalEntityListPage';
import { LegalEntityDetailPage } from '../pages/legalentity/LegalEntityDetailPage';

import { CalendarPage } from '../pages/calendar/CalendarPage';
import { BookingLinksPage } from '../pages/calendar/BookingLinksPage';
import { CalendarSettingsPage } from '../pages/calendar/CalendarSettingsPage';

import { ChatLayout } from '../pages/chat/ChatLayout';
import { ChatDetailPage } from '../pages/chat/ChatDetailPage';
import { ChatCreatePage } from '../pages/chat/ChatCreatePage';
import { ChatEditPage } from '../pages/chat/ChatEditPage';
import { ChatSettingsPage } from '../pages/chat/ChatSettingsPage';
import { ChatPoliciesAdminPage } from '../pages/chat/ChatPoliciesAdminPage';

import { DocumentListPage } from '../pages/document/DocumentListPage';
import { DocumentDetailPage } from '../pages/document/DocumentDetailPage';
import { DocumentCreatePage } from '../pages/document/DocumentCreatePage';
import { DocumentEditPage } from '../pages/document/DocumentEditPage';
import { DocumentSettingsPage } from '../pages/document/DocumentSettingsPage';
import { DocumentDashboardPage } from '../pages/document/DocumentDashboardPage';
import { DocumentTypeListPage } from '../pages/document/DocumentTypeListPage';
import { DocumentTypeDetailPage } from '../pages/document/DocumentTypeDetailPage';
import { DocumentTypeCreatePage } from '../pages/document/DocumentTypeCreatePage';
import { DocumentTypeEditPage } from '../pages/document/DocumentTypeEditPage';
import { DocumentCategoryListPage } from '../pages/document/DocumentCategoryListPage';
import { DocumentCategoryCreatePage } from '../pages/document/DocumentCategoryCreatePage';
import { DocumentCategoryEditPage } from '../pages/document/DocumentCategoryEditPage';
import { DocumentTemplateListPage } from '../pages/document/DocumentTemplateListPage';
import { DocumentTemplateCreatePage } from '../pages/document/DocumentTemplateCreatePage';
import { DocumentFromTemplatePage } from '../pages/document/DocumentFromTemplatePage';
import { DocumentNumberingSchemeListPage } from '../pages/document/DocumentNumberingSchemeListPage';
import { DocumentNumberingSchemeCreatePage } from '../pages/document/DocumentNumberingSchemeCreatePage';
import { DocumentVersionListPage } from '../pages/document/DocumentVersionListPage';
import { DocumentVersionDetailPage } from '../pages/document/DocumentVersionDetailPage';
import { DocumentAcknowledgmentListPage } from '../pages/document/DocumentAcknowledgmentListPage';
import { DocumentRequestAcknowledgmentPage } from '../pages/document/DocumentRequestAcknowledgmentPage';
import { DocumentAuditLogPage } from '../pages/document/DocumentAuditLogPage';
import { DocumentWorkspacePage } from '../pages/document/DocumentWorkspacePage';
import { DocumentClauseLibraryPage } from '../pages/document/DocumentClauseLibraryPage';
import { DocumentTemplateEditorPage } from '../pages/document/DocumentTemplateEditorPage';
import { DocumentAdminPage } from '../pages/document/DocumentAdminPage';

import { TemplateHubPage } from '../pages/templates/TemplateHubPage';

import { FormListPage } from '../pages/form/FormListPage';
import { FormBuilderPage } from '../pages/form/FormBuilderPage';
import { FormTemplatesPage } from '../pages/form/FormTemplatesPage';
import { FormPoliciesPage } from '../pages/form/FormPoliciesPage';

// License routes — status hub (list + detail rail) and signing-key settings
import { LicensesPage } from '../pages/license/LicensesPage';
import { LicenseSettingsPage } from '../pages/license/LicenseSettingsPage';

import { LocalizationKeysPage } from '../pages/localization/LocalizationKeysPage';
import { GlossaryPage } from '../pages/localization/GlossaryPage';
import { LocalizationSettingsPage } from '../pages/localization/LocalizationSettingsPage';

import { NotificationsListPage } from '../pages/notifications/NotificationsListPage';
import { NotificationsDetailPage } from '../pages/notifications/NotificationsDetailPage';
import { ChannelDetailPage } from '../pages/notifications/ChannelDetailPage';
import { ChannelCreatePage } from '../pages/notifications/ChannelCreatePage';
import { ChannelEditPage } from '../pages/notifications/ChannelEditPage';
import { TemplateDetailPage } from '../pages/notifications/TemplateDetailPage';
import { TemplateCreatePage } from '../pages/notifications/TemplateCreatePage';
import { TemplateEditPage } from '../pages/notifications/TemplateEditPage';
import { ContactListPage } from '../pages/notifications/ContactListPage';
import { ContactDetailPage } from '../pages/notifications/ContactDetailPage';
import { ContactCreatePage } from '../pages/notifications/ContactCreatePage';
import { ContactEditPage } from '../pages/notifications/ContactEditPage';
import { TeamSettingsPage as NotificationTeamSettingsPage } from '../pages/notifications/TeamSettingsPage';

import { PlatformListPage } from '../pages/platform/PlatformListPage';
import { PlatformDetailPage } from '../pages/platform/PlatformDetailPage';
import { PlatformCreatePage } from '../pages/platform/PlatformCreatePage';
import { PlatformEditPage } from '../pages/platform/PlatformEditPage';
import { PlatformSettingsPage } from '../pages/platform/PlatformSettingsPage';
import { PlatformMembersPage } from '../pages/platform/PlatformMembersPage';
import { PlatformProductsPage } from '../pages/platform/PlatformProductsPage';
import { PlatformProductConfigPage } from '../pages/platform/PlatformProductConfigPage';
import { PlatformFederationPage } from '../pages/platform/PlatformFederationPage';
import { PlatformFederationPeerPage } from '../pages/platform/PlatformFederationPeerPage';
import { PlatformAuditPage } from '../pages/platform/PlatformAuditPage';
import { PlatformAuthProvidersPage } from '../pages/platform/PlatformAuthProvidersPage';

import { ProductListPage } from '../pages/product/ProductListPage';
import { ProductDetailPage } from '../pages/product/ProductDetailPage';
import { ProductCreatePage } from '../pages/product/ProductCreatePage';
import { ProductEditPage } from '../pages/product/ProductEditPage';
import { ProductSettingsPage } from '../pages/product/ProductSettingsPage';

// Marketplace - Public
import { MarketplaceHomePage } from '../pages/marketplace/MarketplaceHomePage';
import { MarketplaceSearchPage } from '../pages/marketplace/MarketplaceSearchPage';
import { MarketplaceCategoryPage } from '../pages/marketplace/MarketplaceCategoryPage';
import { MarketplaceProductPage } from '../pages/marketplace/MarketplaceProductPage';
import { MarketplaceComparePage } from '../pages/marketplace/MarketplaceComparePage';
import { MarketplaceReviewsPage } from '../pages/marketplace/MarketplaceReviewsPage';

// Marketplace - Tenant
import { TenantProductsPage } from '../pages/marketplace/tenant/TenantProductsPage';
import { TenantProductSetupPage } from '../pages/marketplace/tenant/TenantProductSetupPage';
import { TenantProductDetailPage } from '../pages/marketplace/tenant/TenantProductDetailPage';

// Marketplace - Developer Portal
import { DeveloperDashboardPage } from '../pages/marketplace/developer/DeveloperDashboardPage';
import { DeveloperProductCreatePage } from '../pages/marketplace/developer/DeveloperProductCreatePage';
import { DeveloperProductEditPage } from '../pages/marketplace/developer/DeveloperProductEditPage';
import { DeveloperMarketplacePage } from '../pages/marketplace/developer/DeveloperMarketplacePage';
import { DeveloperDependenciesPage } from '../pages/marketplace/developer/DeveloperDependenciesPage';
import { DeveloperReviewsPage } from '../pages/marketplace/developer/DeveloperReviewsPage';
import { DeveloperPublishPage } from '../pages/marketplace/developer/DeveloperPublishPage';

// Marketplace - Admin
import { AdminProductsPage as MarketplaceAdminProductsPage } from '../pages/marketplace/admin/AdminProductsPage';
import { AdminProductDetailPage as MarketplaceAdminProductDetailPage } from '../pages/marketplace/admin/AdminProductDetailPage';
import { AdminCategoriesPage } from '../pages/marketplace/admin/AdminCategoriesPage';
import { AdminReviewsPage as MarketplaceAdminReviewsPage } from '../pages/marketplace/admin/AdminReviewsPage';
import { AdminVerificationPage } from '../pages/marketplace/admin/AdminVerificationPage';

import { ProjectListPage } from '../pages/project/ProjectListPage';
import { ProjectWorkspacePage } from '../pages/project/ProjectWorkspacePage';
import { ProjectCreatePage } from '../pages/project/ProjectCreatePage';
import { ProjectSettingsDetailsPage } from '../pages/project/ProjectSettingsDetailsPage';
import { ProjectSettingsMembersPage } from '../pages/project/ProjectSettingsMembersPage';
import { ProjectSettingsBoardsPage } from '../pages/project/ProjectSettingsBoardsPage';
import { ProjectSettingsComponentsPage } from '../pages/project/ProjectSettingsComponentsPage';
import { ProjectSettingsLabelsPage } from '../pages/project/ProjectSettingsLabelsPage';
import { ProjectSettingsDangerPage } from '../pages/project/ProjectSettingsDangerPage';

// Entity service config pages (deep-dive from rail)
import { EntityDocumentConfigPage } from '../pages/document/EntityDocumentConfigPage';

// Resource routes — cross-tenant Directory (search, external URLs, types).
// Per-entity linking lives in the right rail (features/resources/), see
// docs/ux/resource-ia.md.
import { ResourceDirectoryPage } from '../pages/resource/ResourceDirectoryPage';

// Search routes — global search (Search + Suggest) and admin index
// management. See docs/ux/search-ia.md.
import { GlobalSearchPage } from '../pages/search/GlobalSearchPage';
import { AdminSearchPage } from '../pages/search/admin/AdminSearchPage';

import { SecretConsolePage } from '../pages/secret/SecretConsolePage';
import { SecretLeasesPage } from '../pages/secret/SecretLeasesPage';
import { SecretPoliciesPage } from '../pages/secret/SecretPoliciesPage';
import { SecretAuditPage } from '../pages/secret/SecretAuditPage';

import { SubscriptionListPage } from '../pages/subscription/SubscriptionListPage';
import { SubscriptionDetailPage } from '../pages/subscription/SubscriptionDetailPage';
import { SubscriptionCreatePage } from '../pages/subscription/SubscriptionCreatePage';
import { SubscriptionEditPage } from '../pages/subscription/SubscriptionEditPage';
import { SubscriptionSettingsPage } from '../pages/subscription/SubscriptionSettingsPage';
import { SubscriptionDashboardPage } from '../pages/subscription/SubscriptionDashboardPage';
import { SubscriptionChangePlanPage } from '../pages/subscription/SubscriptionChangePlanPage';
import { SubscriptionItemsPage } from '../pages/subscription/SubscriptionItemsPage';
import { SubscriptionUsagePage } from '../pages/subscription/SubscriptionUsagePage';
import { SubscriptionHistoryPage } from '../pages/subscription/SubscriptionHistoryPage';
import { SubscriptionAnalyticsPage } from '../pages/subscription/SubscriptionAnalyticsPage';
import { SubscriptionAuditLogPage } from '../pages/subscription/SubscriptionAuditLogPage';
import { PlanListPage } from '../pages/subscription/PlanListPage';
import { PlanCreatePage } from '../pages/subscription/PlanCreatePage';
import { PlanDetailPage } from '../pages/subscription/PlanDetailPage';
import { PlanEditPage } from '../pages/subscription/PlanEditPage';
import { DiscountListPage as SubDiscountListPage } from '../pages/subscription/DiscountListPage';
import { DiscountCreatePage as SubDiscountCreatePage } from '../pages/subscription/DiscountCreatePage';
import { DiscountDetailPage as SubDiscountDetailPage } from '../pages/subscription/DiscountDetailPage';
import { DiscountEditPage as SubDiscountEditPage } from '../pages/subscription/DiscountEditPage';
import { TenantSubscriptionListPage } from '../pages/subscription/TenantSubscriptionListPage';
import { TenantSubscriptionDetailPage } from '../pages/subscription/TenantSubscriptionDetailPage';
import { TenantSubscribePage } from '../pages/subscription/TenantSubscribePage';
import { EntitlementListPage } from '../pages/subscription/EntitlementListPage';
import { EntitlementCheckPage } from '../pages/subscription/EntitlementCheckPage';
import { EntitlementDetailPage } from '../pages/subscription/EntitlementDetailPage';
import { AddOnListPage } from '../pages/subscription/AddOnListPage';
import { AddOnCreatePage } from '../pages/subscription/AddOnCreatePage';
import { AddOnDetailPage } from '../pages/subscription/AddOnDetailPage';
import { AddOnEditPage } from '../pages/subscription/AddOnEditPage';
import { AddOnPurchasePage } from '../pages/subscription/AddOnPurchasePage';
import { BundleListPage } from '../pages/subscription/BundleListPage';
import { BundleCreatePage } from '../pages/subscription/BundleCreatePage';
import { BundleDetailPage } from '../pages/subscription/BundleDetailPage';
import { BundleEditPage } from '../pages/subscription/BundleEditPage';
import { BundleSubscribePage } from '../pages/subscription/BundleSubscribePage';
import { BundleSubscriptionListPage } from '../pages/subscription/BundleSubscriptionListPage';
import { BundleSubscriptionDetailPage } from '../pages/subscription/BundleSubscriptionDetailPage';

import { TagListPage } from '../pages/tag/TagListPage';
import { TagDetailPage } from '../pages/tag/TagDetailPage';
import { TagCreatePage } from '../pages/tag/TagCreatePage';
import { TagEditPage } from '../pages/tag/TagEditPage';
import { TagSettingsPage } from '../pages/tag/TagSettingsPage';

import { TaskWorkspacePage } from '../pages/task/TaskWorkspacePage';
import { TaskDetailPage } from '../pages/task/TaskDetailPage';
import { TaskCreatePage } from '../pages/task/TaskCreatePage';
import { TaskEditPage } from '../pages/task/TaskEditPage';
import { TaskSettingsPage } from '../pages/task/TaskSettingsPage';
import { MyWorkPage } from '../pages/my/MyWorkPage';
import { TaskStatsPage } from '../pages/task/TaskStatsPage';
import { TaskTemplateListPage } from '../pages/task/TaskTemplateListPage';
import { TaskTemplateCreatePage } from '../pages/task/TaskTemplateCreatePage';
import { TaskTemplateDetailPage } from '../pages/task/TaskTemplateDetailPage';
import { TaskTemplateEditPage } from '../pages/task/TaskTemplateEditPage';
import { TaskTypeListPage } from '../pages/task/TaskTypeListPage';
import { TaskTypeCreatePage } from '../pages/task/TaskTypeCreatePage';
import { TaskTypeEditPage } from '../pages/task/TaskTypeEditPage';
import { TaskReadyPage } from '../pages/task/TaskReadyPage';
import { TaskDependencyGraphPage } from '../pages/task/TaskDependencyGraphPage';
import { TaskAdminPage } from '../pages/task/TaskAdminPage';

import { TeamListPage } from '../pages/team/TeamListPage';
import { TeamDetailPage } from '../pages/team/TeamDetailPage';
import { TeamWorkspacePage } from '../pages/team/TeamWorkspacePage';
import { TeamCreatePage } from '../pages/team/TeamCreatePage';
import { TeamEditPage } from '../pages/team/TeamEditPage';
import { TeamSettingsPage } from '../pages/team/TeamSettingsPage';
import { TeamMembersPage } from '../pages/team/TeamMembersPage';
import { TeamMemberDetailPage } from '../pages/team/TeamMemberDetailPage';
import { TeamRolesPage } from '../pages/team/TeamRolesPage';
import { TeamRoleDetailPage } from '../pages/team/TeamRoleDetailPage';
import { TeamApplicationsPage } from '../pages/team/TeamApplicationsPage';
import { TeamOwnershipPage } from '../pages/team/TeamOwnershipPage';
import { TeamAuditLogPage } from '../pages/team/TeamAuditLogPage';
import { TeamBrandingPage } from '../pages/team/TeamBrandingPage';
import { TeamSchedulesPage } from '../pages/team/TeamSchedulesPage';
import { TeamScheduleDetailPage } from '../pages/team/TeamScheduleDetailPage';
import { TeamDelegationsPage } from '../pages/team/TeamDelegationsPage';
import { TeamDelegationDetailPage } from '../pages/team/TeamDelegationDetailPage';
import { TeamTemporaryRolesPage } from '../pages/team/TeamTemporaryRolesPage';
import { TeamAutomationEventsPage } from '../pages/team/TeamAutomationEventsPage';
import { TeamUserSettingsPage } from '../pages/team/TeamUserSettingsPage';

import { TenantListPage } from '../pages/tenant/TenantListPage';
import { TenantDetailPage } from '../pages/tenant/TenantDetailPage';
import { TenantCreatePage } from '../pages/tenant/TenantCreatePage';
import { TenantEditPage } from '../pages/tenant/TenantEditPage';
import { TenantSettingsPage } from '../pages/tenant/TenantSettingsPage';
import { TenantSwitcherPage } from '../pages/tenant/TenantSwitcherPage';
import { TenantDashboardPage } from '../pages/tenant/TenantDashboardPage';
import { TenantMembersPage } from '../pages/tenant/TenantMembersPage';
import { TenantOnboardingPage } from '../pages/tenant/TenantOnboardingPage';
import { TenantGeneralSettingsPage } from '../pages/tenant/TenantGeneralSettingsPage';
import { TenantBrandingPage } from '../pages/tenant/TenantBrandingPage';
import { TenantSecurityPage } from '../pages/tenant/TenantSecurityPage';
import { TenantSSOPage } from '../pages/tenant/TenantSSOPage';
import { TenantQuotasPage } from '../pages/tenant/TenantQuotasPage';
import { TenantDataExportPage } from '../pages/tenant/TenantDataExportPage';
import { TenantDangerZonePage } from '../pages/tenant/TenantDangerZonePage';

// Tenant - Admin
import { AdminTenantsPage } from '../pages/tenant/admin/AdminTenantsPage';
import { AdminTenantDetailPage } from '../pages/tenant/admin/AdminTenantDetailPage';
import { AdminMigrationsPage } from '../pages/tenant/admin/AdminMigrationsPage';

import { UserListPage } from '../pages/user/UserListPage';
import { UserDetailPage } from '../pages/user/UserDetailPage';
import { UserEditPage } from '../pages/user/UserEditPage';
import { UserSettingsPage } from '../pages/user/UserSettingsPage';

import { UserprofileListPage } from '../pages/userprofile/UserprofileListPage';
import { UserprofileDetailPage } from '../pages/userprofile/UserprofileDetailPage';
import { UserprofileCreatePage } from '../pages/userprofile/UserprofileCreatePage';
import { UserprofileEditPage } from '../pages/userprofile/UserprofileEditPage';
import { UserprofileSettingsPage } from '../pages/userprofile/UserprofileSettingsPage';
import { ProfileSettingsPage } from '../pages/userprofile/ProfileSettingsPage';
import { AdminUserprofilePage } from '../pages/userprofile/admin/AdminUserprofilePage';

import { PreferencesPage } from '../pages/userprofile/PreferencesPage';
import { ApiKeysPage } from '../pages/userprofile/ApiKeysPage';
import { WebhooksPage } from '../pages/userprofile/WebhooksPage';
import { InvitationsPage } from '../pages/user/InvitationsPage';

import { WebhookListPage } from '../pages/webhook/WebhookListPage';
import { WebhookDetailPage } from '../pages/webhook/WebhookDetailPage';
import { WebhookSettingsPage } from '../pages/webhook/WebhookSettingsPage';

import { WorkflowListPage } from '../pages/workflow/WorkflowListPage';
import { WorkflowDetailPage } from '../pages/workflow/WorkflowDetailPage';
import { WorkflowCreatePage } from '../pages/workflow/WorkflowCreatePage';
import { WorkflowEditPage } from '../pages/workflow/WorkflowEditPage';
import { WorkflowSettingsPage } from '../pages/workflow/WorkflowSettingsPage';
import { ExecutionDetailPage } from '../pages/workflow/ExecutionDetailPage';
import { ApprovalsPage } from '../pages/workflow/ApprovalsPage';
import { CredentialListPage } from '../pages/workflow/CredentialListPage';
import { NodeTypeListPage } from '../pages/workflow/NodeTypeListPage';
import { PipelinePolicyListPage } from '../pages/workflow/PipelinePolicyListPage';
import { TemplateListPage as WorkflowTemplateListPage } from '../pages/workflow/TemplateListPage';
import { PromptTemplateListPage } from '../pages/workflow/PromptTemplateListPage';
import { PromptTemplateEditPage } from '../pages/workflow/PromptTemplateEditPage';
import { AgentToolListPage } from '../pages/workflow/AgentToolListPage';
import { AgentToolEditPage } from '../pages/workflow/AgentToolEditPage';


import { AdminRoute } from '../components/AdminRoute';

/**
 * Every page route of the app. The parent route is a bare pass-through so the
 * child paths stay relative exactly as they were under AppShell.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Outlet />}>
          <Route index element={<Navigate to="/inbox" replace />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="my" element={<MyWorkPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          {/* Dev-only living reference for docs/ux/14-forms.md */}
          <Route path="dev/forms" element={<FormGalleryPage />} />
          
          {/* Activity routes — inbox (home), feed, decisions; settings tabbed
              (preferences for everyone, mappings admin-gated inside the page) */}
          <Route path="activity" element={<ActivityPage />} />
          <Route path="activity/settings" element={<ActivitySettingsPage />} />

          {/* Admin routes — dashboard/settings/audit-log, all admin-gated (docs/ux/admin-ia.md) */}
          <Route path="admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
          <Route path="admins" element={<Navigate to="/admin/settings" replace />} />
          <Route path="admins/:id" element={<Navigate to="/admin/settings" replace />} />
          <Route path="admins/:id/edit" element={<Navigate to="/admin/settings" replace />} />
          <Route path="admins/settings" element={<Navigate to="/admin/settings" replace />} />

          {/* Affiliate routes — partner dashboard + admin area (product-admin gated server-side) */}
          <Route path="affiliate" element={<AffiliateDashboardPage />} />
          <Route path="affiliate/offers" element={<AffiliateOffersPage />} />
          <Route path="affiliate/campaigns" element={<AffiliateCampaignsPage />} />
          <Route path="affiliate/payouts" element={<AffiliatePayoutsPage />} />
          <Route path="affiliate/admin" element={<AdminRoute><AffiliateAdminPage /></AdminRoute>} />

          {/* Analytics routes — dashboards hub + view, saved queries, reports/schedules */}
          <Route path="analytics" element={<DashboardsHubPage />} />
          <Route path="analytics/queries" element={<SavedQueriesPage />} />
          <Route path="analytics/reports" element={<AnalyticsReportsPage />} />
          <Route path="analytics/:dashboardId" element={<DashboardViewPage />} />

          {/* Landing routes — pages hub, editor, template gallery (public render is outside ProtectedRoute, see /l/:slug above) */}
          <Route path="landing" element={<LandingPagesListPage />} />
          <Route path="landing/templates" element={<LandingTemplatesPage />} />
          <Route path="landing/:pageId" element={<LandingPageEditorPage />} />
          
          {/* Apikey routes — console (docs/ux/apikey-ia.md). Personal "my keys" page lives at
              /settings (pages/userprofile/ApiKeysPage.tsx), untouched. */}
          <Route path="apikeys" element={<ApikeyListPage />} />
          <Route path="apikeys/policies" element={<AdminRoute><ApikeyPoliciesPage /></AdminRoute>} />
          <Route path="apikeys/new" element={<Navigate to="/apikeys" replace />} />
          <Route path="apikeys/:id" element={<Navigate to="/apikeys" replace />} />
          <Route path="apikeys/:id/edit" element={<Navigate to="/apikeys" replace />} />
          <Route path="apikeys/settings" element={<Navigate to="/apikeys/policies" replace />} />

          {/* Attachment routes — Manager (docs/ux/attachment-ia.md). Legacy
              scaffold paths redirect to the designed hub. */}
          <Route path="attachments" element={<AttachmentManagerPage />} />
          <Route path="attachments/:id" element={<AttachmentManagerPage />} />
          <Route path="attachments/:id/edit" element={<Navigate to=".." relative="path" replace />} />
          <Route path="attachments/settings" element={<Navigate to="/attachments" replace />} />
          
          {/* Audittrail routes — compliance explorer (docs/ux/audittrail-ia.md), admin-gated */}
          <Route path="audit" element={<AdminRoute><AuditExplorerPage /></AdminRoute>} />
          <Route path="audittrails" element={<Navigate to="/audit" replace />} />
          <Route path="audittrails/new" element={<Navigate to="/audit" replace />} />
          <Route path="audittrails/:id" element={<Navigate to="/audit" replace />} />
          <Route path="audittrails/:id/edit" element={<Navigate to="/audit" replace />} />
          <Route path="audittrails/settings" element={<Navigate to="/audit" replace />} />
          
          {/* Settings routes — wrapped in SettingsLayout with sidebar nav */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="/settings/profile" replace />} />
            <Route path="profile" element={<ProfileSettingsPage />} />
            <Route path="contacts" element={<ContactListPage />} />
            <Route path="contacts/new" element={<ContactCreatePage />} />
            <Route path="contacts/:id" element={<ContactDetailPage />} />
            <Route path="contacts/:id/edit" element={<ContactEditPage />} />
            <Route path="security" element={<SecurityPage />} />
            {/* 2FA setup is inline on SecurityPage */}
            <Route path="sessions" element={<SessionsPage />} />
            <Route path="preferences" element={<PreferencesPage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="consents" element={<OAuthConsentsPage />} />
            <Route path="webhooks" element={<WebhooksPage />} />
            <Route path="invitations" element={<InvitationsPage />} />
          </Route>

          {/* Authn routes */}
          <Route path="authns" element={<AuthnListPage />} />
          <Route path="authns/new" element={<AuthnCreatePage />} />
          <Route path="authns/:id" element={<AuthnDetailPage />} />
          <Route path="authns/:id/edit" element={<AuthnEditPage />} />
          <Route path="authns/settings" element={<AuthnSettingsPage />} />
          
          {/* Admin/infra inspectors — bootstrap wizard, context resolution, authz. See
              docs/ux/bootstrap-ia.md, docs/ux/context-ia.md, docs/ux/authz-ia.md. */}
          <Route path="admin/bootstrap" element={<AdminRoute><BootstrapAdminPage /></AdminRoute>} />
          <Route path="admin/context" element={<AdminRoute><ContextAdminPage /></AdminRoute>} />
          <Route path="admin/authz" element={<AdminRoute><AuthzInspectorPage /></AdminRoute>} />
          {/* Authn admin console — SSO/OIDC provider registry, discovery, token tools.
              See docs/ux/authn-ia.md. */}
          <Route path="admin/authn" element={<AdminRoute><AuthnAdminPage /></AdminRoute>} />
          {/* Legacy scaffold path redirects to the designed inspector */}
          <Route path="authzs" element={<Navigate to="/admin/authz" replace />} />
          <Route path="authzs/:id" element={<Navigate to="/admin/authz" replace />} />
          <Route path="authzs/settings" element={<Navigate to="/admin/authz" replace />} />

          {/* Signature routes — verify lookup is open to any authenticated user;
              party registry is admin-only. See docs/ux/signature-ia.md. */}
          <Route path="signature/verify" element={<SignatureVerifyPage />} />
          <Route path="signature/parties" element={<AdminRoute><SignaturePartiesPage /></AdminRoute>} />
          
          {/* Billing routes */}
          <Route path="billing" element={<BillingDashboardPage />} />
          <Route path="billing/settings" element={<BillingSettingsPage />} />

          {/* Billing - Chart of Accounts (create is a dialog on BillingListPage; edit is inline on BillingDetailPage) */}
          <Route path="billing/chart-of-accounts" element={<BillingListPage />} />
          <Route path="billing/chart-of-accounts/:id" element={<BillingDetailPage />} />

          {/* Billing - Billing Accounts (create is a dialog on BillingAccountListPage; edit is inline on BillingAccountDetailPage) */}
          <Route path="billing/accounts" element={<BillingAccountListPage />} />
          <Route path="billing/accounts/:id" element={<BillingAccountDetailPage />} />

          {/* Billing - Invoices */}
          <Route path="billing/invoices" element={<InvoiceListPage />} />
          <Route path="billing/invoices/new" element={<InvoiceCreatePage />} />
          <Route path="billing/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="billing/invoices/:id/edit" element={<InvoiceEditPage />} />

          {/* Billing - Transactions */}
          <Route path="billing/transactions" element={<TransactionListPage />} />
          <Route path="billing/transactions/new" element={<TransactionCreatePage />} />
          <Route path="billing/transactions/:id" element={<TransactionDetailPage />} />

          {/* Billing - Products & Prices */}
          <Route path="billing/products" element={<BillingProductListPage />} />
          <Route path="billing/products/new" element={<BillingProductCreatePage />} />
          <Route path="billing/products/:id" element={<BillingProductDetailPage />} />
          <Route path="billing/products/:id/edit" element={<BillingProductEditPage />} />
          <Route path="billing/prices/:id" element={<PriceDetailPage />} />

          {/* Billing - Usage */}
          <Route path="billing/usage" element={<UsageListPage />} />
          <Route path="billing/usage/new" element={<UsageCreatePage />} />
          <Route path="billing/usage/summary" element={<UsageSummaryPage />} />

          {/* Billing - Journal Entries */}
          <Route path="billing/journal-entries" element={<JournalEntryListPage />} />
          <Route path="billing/journal-entries/new" element={<JournalEntryCreatePage />} />
          <Route path="billing/journal-entries/:id" element={<JournalEntryDetailPage />} />

          {/* Billing - Ledger & Reports */}
          <Route path="billing/ledger" element={<LedgerPage />} />
          <Route path="billing/reports/trial-balance" element={<TrialBalancePage />} />
          <Route path="billing/reports/revenue" element={<RevenueReportPage />} />

          
          {/* Booking routes — resources catalog (home), resource detail with
              booking calendar, my bookings, provider/admin area */}
          <Route path="booking" element={<ResourcesCatalogPage />} />
          <Route path="booking/resources/:id" element={<BookingResourceDetailPage />} />
          <Route path="booking/my-bookings" element={<MyBookingsPage />} />
          <Route path="booking/admin" element={<BookingAdminPage />} />
          {/* Legacy scaffold paths redirect to the designed pages */}
          <Route path="bookings" element={<Navigate to="/booking" replace />} />
          <Route path="bookings/new" element={<Navigate to="/booking" replace />} />
          <Route path="bookings/:id" element={<Navigate to="/booking/my-bookings" replace />} />
          <Route path="bookings/:id/edit" element={<Navigate to="/booking/my-bookings" replace />} />
          <Route path="bookings/settings" element={<Navigate to="/booking/admin" replace />} />

          {/* Legal entity routes — list + detail/edit with verification panel */}
          <Route path="legalentity" element={<LegalEntityListPage />} />
          <Route path="legalentity/:id" element={<LegalEntityDetailPage />} />


          {/* Payment routes — payment methods live on the billing account page;
              this is providers, routing/policy/type config, and the payments ledger. */}
          <Route path="payment/providers" element={<PaymentProvidersPage />} />
          <Route path="payment/settings" element={<PaymentSettingsPage />} />
          <Route path="payment/payments" element={<PaymentsLedgerPage />} />

          {/* Fiscalization routes — ops dashboard, fiscal points/shifts, receipts, admin config */}
          <Route path="fiscalization" element={<FiscalizationDashboardPage />} />
          <Route path="fiscalization/points" element={<FiscalPointsPage />} />
          <Route path="fiscalization/receipts" element={<FiscalReceiptsPage />} />
          <Route path="fiscalization/settings" element={<FiscalizationSettingsPage />} />
          <Route path="fiscalization/reports/summary" element={<FiscalSummaryReportPage />} />

          {/* Calendar routes — grid hub, booking links, settings */}
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="calendar/booking" element={<BookingLinksPage />} />
          <Route path="calendar/settings" element={<CalendarSettingsPage />} />
          {/* Legacy scaffold paths redirect to the designed pages */}
          <Route path="calendars" element={<Navigate to="/calendar" replace />} />
          <Route path="calendars/new" element={<Navigate to="/calendar/settings?tab=calendars" replace />} />
          <Route path="calendars/settings" element={<Navigate to="/calendar/settings" replace />} />
          <Route path="calendars/:id" element={<Navigate to="/calendar" replace />} />
          <Route path="calendars/:id/edit" element={<Navigate to="/calendar/settings?tab=calendars" replace />} />
          
          {/* Chat routes — master-detail layout */}
          <Route path="chats" element={<ChatLayout />}>
            <Route path="new" element={<ChatCreatePage />} />
            <Route path=":id" element={<ChatDetailPage />} />
          </Route>
          <Route path="chats/:id/edit" element={<ChatEditPage />} />
          <Route path="chats/:id/settings" element={<ChatSettingsPage />} />
          <Route path="admin/chat" element={<AdminRoute><ChatPoliciesAdminPage /></AdminRoute>} />

          {/* Document routes */}
          <Route path="documents" element={<DocumentListPage />} />
          <Route path="documents/new" element={<DocumentCreatePage />} />
          <Route path="documents/dashboard" element={<DocumentDashboardPage />} />
          <Route path="documents/settings" element={<DocumentSettingsPage />} />
          <Route path="documents/types" element={<DocumentTypeListPage />} />
          <Route path="documents/types/new" element={<DocumentTypeCreatePage />} />
          <Route path="documents/types/:typeId" element={<DocumentTypeDetailPage />} />
          <Route path="documents/types/:typeId/edit" element={<DocumentTypeEditPage />} />
          <Route path="documents/categories" element={<DocumentCategoryListPage />} />
          <Route path="documents/categories/new" element={<DocumentCategoryCreatePage />} />
          <Route path="documents/categories/:categoryId/edit" element={<DocumentCategoryEditPage />} />
          <Route path="documents/templates" element={<DocumentTemplateListPage />} />
          <Route path="documents/templates/new" element={<DocumentTemplateCreatePage />} />
          <Route path="documents/templates/:templateId/create" element={<DocumentFromTemplatePage />} />
          <Route path="documents/templates/:templateId" element={<DocumentTemplateEditorPage />} />
          <Route path="documents/clauses" element={<DocumentClauseLibraryPage />} />
          <Route path="documents/admin" element={<DocumentAdminPage />} />
          <Route path="documents/numbering-schemes" element={<DocumentNumberingSchemeListPage />} />
          <Route path="documents/numbering-schemes/new" element={<DocumentNumberingSchemeCreatePage />} />
          <Route path="documents/:id" element={<DocumentDetailPage />} />
          <Route path="documents/:id/edit" element={<DocumentEditPage />} />
          <Route path="documents/:id/versions" element={<DocumentVersionListPage />} />
          <Route path="documents/:id/versions/:versionId" element={<DocumentVersionDetailPage />} />
          <Route path="documents/:id/acknowledgments" element={<DocumentAcknowledgmentListPage />} />
          <Route path="documents/:id/request-acknowledgment" element={<DocumentRequestAcknowledgmentPage />} />
          <Route path="documents/:id/audit-log" element={<DocumentAuditLogPage />} />
          <Route path="docs/workspace" element={<DocumentWorkspacePage />} />

          {/* Template Hub */}
          <Route path="templates" element={<TemplateHubPage />} />

          {/* Form routes — see docs/ux/form-ia.md */}
          <Route path="forms" element={<FormListPage />} />
          <Route path="forms/templates" element={<FormTemplatesPage />} />
          <Route path="forms/policies" element={<FormPoliciesPage />} />
          <Route path="forms/:id" element={<FormBuilderPage />} />

          {/* License routes — status hub + detail rail, signing-key settings. Admin-only (platform admin). */}
          <Route path="license" element={<AdminRoute><LicensesPage /></AdminRoute>} />
          <Route path="license/settings" element={<AdminRoute><LicenseSettingsPage /></AdminRoute>} />
          {/* Legacy scaffold paths redirect to the designed hub */}
          <Route path="licenses" element={<Navigate to="/license" replace />} />
          <Route path="licenses/:id" element={<Navigate to="/license" replace />} />
          <Route path="licenses/:id/edit" element={<Navigate to="/license" replace />} />
          <Route path="licenses/settings" element={<Navigate to="/license/settings" replace />} />

          {/* Localization routes — see docs/ux/localization-ia.md */}
          <Route path="localization" element={<LocalizationKeysPage />} />
          <Route path="localization/glossary" element={<GlossaryPage />} />
          <Route path="localization/settings" element={<LocalizationSettingsPage />} />
          <Route path="localizations" element={<Navigate to="/localization" replace />} />

          {/* Notifications routes */}
          {/* Notifications — single tabbed page + detail/create/edit sub-routes */}
          <Route path="notifications" element={<NotificationsListPage />} />
          <Route path="notifications/:id" element={<NotificationsDetailPage />} />
          <Route path="notifications/channels/new" element={<AdminRoute><ChannelCreatePage /></AdminRoute>} />
          <Route path="notifications/channels/:id" element={<AdminRoute><ChannelDetailPage /></AdminRoute>} />
          <Route path="notifications/channels/:id/edit" element={<AdminRoute><ChannelEditPage /></AdminRoute>} />
          <Route path="notifications/templates/new" element={<AdminRoute><TemplateCreatePage /></AdminRoute>} />
          <Route path="notifications/templates/:id" element={<AdminRoute><TemplateDetailPage /></AdminRoute>} />
          <Route path="notifications/templates/:id/edit" element={<AdminRoute><TemplateEditPage /></AdminRoute>} />
          <Route path="notifications/contacts/new" element={<AdminRoute><ContactCreatePage /></AdminRoute>} />
          <Route path="notifications/contacts/:id" element={<AdminRoute><ContactDetailPage /></AdminRoute>} />
          <Route path="notifications/contacts/:id/edit" element={<AdminRoute><ContactEditPage /></AdminRoute>} />
          <Route path="notifications/team/:teamId" element={<NotificationTeamSettingsPage />} />
          
          {/* Platform routes */}
          <Route path="platforms" element={<PlatformListPage />} />
          <Route path="platforms/new" element={<PlatformCreatePage />} />
          <Route path="platforms/:id" element={<PlatformDetailPage />} />
          <Route path="platforms/:id/edit" element={<PlatformEditPage />} />
          <Route path="platforms/:id/settings" element={<PlatformSettingsPage />} />
          <Route path="platforms/:id/members" element={<PlatformMembersPage />} />
          <Route path="platforms/:id/products" element={<PlatformProductsPage />} />
          <Route path="platforms/:id/products/:productId" element={<PlatformProductConfigPage />} />
          <Route path="platforms/:id/federation" element={<PlatformFederationPage />} />
          <Route path="platforms/:id/federation/:peerId" element={<PlatformFederationPeerPage />} />
          <Route path="platforms/:id/audit" element={<PlatformAuditPage />} />
          <Route path="platforms/:id/auth-providers" element={<PlatformAuthProvidersPage />} />
          
          {/* Product routes */}
          <Route path="products" element={<ProductListPage />} />
          <Route path="products/new" element={<ProductCreatePage />} />
          <Route path="products/:id" element={<ProductDetailPage />} />
          <Route path="products/:id/edit" element={<ProductEditPage />} />
          <Route path="products/settings" element={<ProductSettingsPage />} />

          {/* Marketplace - Public */}
          <Route path="marketplace" element={<MarketplaceHomePage />} />
          <Route path="marketplace/search" element={<MarketplaceSearchPage />} />
          <Route path="marketplace/compare" element={<MarketplaceComparePage />} />
          <Route path="marketplace/categories/:slug" element={<MarketplaceCategoryPage />} />
          <Route path="marketplace/:slug" element={<MarketplaceProductPage />} />
          <Route path="marketplace/:slug/reviews" element={<MarketplaceReviewsPage />} />

          {/* Marketplace - Tenant Product Management */}
          <Route path="settings/products" element={<TenantProductsPage />} />
          <Route path="settings/products/:id/setup" element={<TenantProductSetupPage />} />
          <Route path="settings/products/:id" element={<TenantProductDetailPage />} />

          {/* Developer Portal */}
          <Route path="developer" element={<DeveloperDashboardPage />} />
          <Route path="developer/products/new" element={<DeveloperProductCreatePage />} />
          <Route path="developer/products/:id/edit" element={<DeveloperProductEditPage />} />
          <Route path="developer/products/:id/marketplace" element={<DeveloperMarketplacePage />} />
          <Route path="developer/products/:id/dependencies" element={<DeveloperDependenciesPage />} />
          <Route path="developer/products/:id/reviews" element={<DeveloperReviewsPage />} />
          <Route path="developer/products/:id/publish" element={<DeveloperPublishPage />} />

          {/* Admin - Marketplace Management */}
          <Route path="admin/products" element={<MarketplaceAdminProductsPage />} />
          <Route path="admin/products/:id" element={<MarketplaceAdminProductDetailPage />} />
          <Route path="admin/products/categories" element={<AdminCategoriesPage />} />
          <Route path="admin/products/reviews" element={<MarketplaceAdminReviewsPage />} />
          <Route path="admin/products/verification" element={<AdminVerificationPage />} />
          
          {/* Project routes */}
          <Route path="projects" element={<ProjectListPage />} />
          <Route path="projects/new" element={<ProjectCreatePage />} />
          <Route path="projects/:id" element={<ProjectWorkspacePage />} />
          <Route path="projects/:id/detail" element={<Navigate to=".." relative="path" replace />} />
          <Route path="projects/:id/edit" element={<Navigate to="../settings" relative="path" replace />} />
          <Route path="projects/:id/settings" element={<ProjectSettingsLayout />}>
            <Route index element={<ProjectSettingsDetailsPage />} />
            <Route path="members" element={<ProjectSettingsMembersPage />} />
            <Route path="boards" element={<ProjectSettingsBoardsPage />} />
            <Route path="components" element={<ProjectSettingsComponentsPage />} />
            <Route path="labels" element={<ProjectSettingsLabelsPage />} />
            <Route path="danger" element={<ProjectSettingsDangerPage />} />
          </Route>
          {/* Entity service config (deep-dive from rail) */}
          <Route path="projects/:id/services/documents" element={<EntityDocumentConfigPage entityType="project" />} />

          {/* Resource routes — Directory (docs/ux/resource-ia.md). Legacy
              scaffold paths redirect; per-entity linking is in the rail. */}
          <Route path="resources" element={<ResourceDirectoryPage />} />
          <Route path="resources/new" element={<Navigate to="/resources" replace />} />
          <Route path="resources/:id" element={<Navigate to="/resources" replace />} />
          <Route path="resources/:id/edit" element={<Navigate to="/resources" replace />} />
          <Route path="resources/settings" element={<Navigate to="/resources" replace />} />
          
          {/* Search routes — global search (docs/ux/search-ia.md); admin
              index management is platform-admin-gated. Legacy scaffold
              paths (/searchs/...) redirect. */}
          <Route path="search" element={<GlobalSearchPage />} />
          <Route path="admin/search" element={<AdminRoute><AdminSearchPage /></AdminRoute>} />
          <Route path="searchs" element={<Navigate to="/search" replace />} />
          <Route path="searchs/new" element={<Navigate to="/admin/search" replace />} />
          <Route path="searchs/:id" element={<Navigate to="/admin/search" replace />} />
          <Route path="searchs/:id/edit" element={<Navigate to="/admin/search" replace />} />
          <Route path="searchs/settings" element={<Navigate to="/admin/search" replace />} />
          
          {/* Secret routes — Vault-like console (docs/ux/secret-ia.md). Fully admin-gated. */}
          <Route path="secrets" element={<AdminRoute><SecretConsolePage /></AdminRoute>} />
          <Route path="secrets/leases" element={<AdminRoute><SecretLeasesPage /></AdminRoute>} />
          <Route path="secrets/policies" element={<AdminRoute><SecretPoliciesPage /></AdminRoute>} />
          <Route path="secrets/audit" element={<AdminRoute><SecretAuditPage /></AdminRoute>} />
          <Route path="secrets/new" element={<Navigate to="/secrets" replace />} />
          <Route path="secrets/:id" element={<Navigate to="/secrets" replace />} />
          <Route path="secrets/:id/edit" element={<Navigate to="/secrets" replace />} />
          <Route path="secrets/settings" element={<Navigate to="/secrets" replace />} />

          {/* Subscription routes */}
          <Route path="subscriptions" element={<SubscriptionListPage />} />
          <Route path="subscriptions/dashboard" element={<SubscriptionDashboardPage />} />
          <Route path="subscriptions/new" element={<SubscriptionCreatePage />} />
          <Route path="subscriptions/settings" element={<SubscriptionSettingsPage />} />
          <Route path="subscriptions/analytics" element={<SubscriptionAnalyticsPage />} />
          <Route path="subscriptions/audit-log" element={<SubscriptionAuditLogPage />} />
          <Route path="subscriptions/:id" element={<SubscriptionDetailPage />} />
          <Route path="subscriptions/:id/edit" element={<SubscriptionEditPage />} />
          <Route path="subscriptions/:id/change-plan" element={<SubscriptionChangePlanPage />} />
          <Route path="subscriptions/:id/items" element={<SubscriptionItemsPage />} />
          <Route path="subscriptions/:id/usage" element={<SubscriptionUsagePage />} />
          <Route path="subscriptions/:id/history" element={<SubscriptionHistoryPage />} />
          <Route path="subscriptions/:id/audit-log" element={<SubscriptionAuditLogPage />} />

          {/* Subscription - Plans */}
          <Route path="subscriptions/plans" element={<PlanListPage />} />
          <Route path="subscriptions/plans/new" element={<PlanCreatePage />} />
          <Route path="subscriptions/plans/:id" element={<PlanDetailPage />} />
          <Route path="subscriptions/plans/:id/edit" element={<PlanEditPage />} />

          {/* Subscription - Discounts */}
          <Route path="subscriptions/discounts" element={<SubDiscountListPage />} />
          <Route path="subscriptions/discounts/new" element={<SubDiscountCreatePage />} />
          <Route path="subscriptions/discounts/:id" element={<SubDiscountDetailPage />} />
          <Route path="subscriptions/discounts/:id/edit" element={<SubDiscountEditPage />} />

          {/* Subscription - Tenant Subscriptions */}
          <Route path="subscriptions/tenants" element={<TenantSubscriptionListPage />} />
          <Route path="subscriptions/tenants/new" element={<TenantSubscribePage />} />
          <Route path="subscriptions/tenants/:id" element={<TenantSubscriptionDetailPage />} />

          {/* Subscription - Entitlements */}
          <Route path="subscriptions/entitlements" element={<EntitlementListPage />} />
          <Route path="subscriptions/entitlements/check" element={<EntitlementCheckPage />} />
          <Route path="subscriptions/entitlements/:id" element={<EntitlementDetailPage />} />

          {/* Subscription - Add-Ons */}
          <Route path="subscriptions/addons" element={<AddOnListPage />} />
          <Route path="subscriptions/addons/new" element={<AddOnCreatePage />} />
          <Route path="subscriptions/addons/:id" element={<AddOnDetailPage />} />
          <Route path="subscriptions/addons/:id/edit" element={<AddOnEditPage />} />
          <Route path="subscriptions/addons/:id/purchase" element={<AddOnPurchasePage />} />

          {/* Subscription - Bundles */}
          <Route path="subscriptions/bundles" element={<BundleListPage />} />
          <Route path="subscriptions/bundles/new" element={<BundleCreatePage />} />
          <Route path="subscriptions/bundles/subscriptions" element={<BundleSubscriptionListPage />} />
          <Route path="subscriptions/bundles/subscriptions/:id" element={<BundleSubscriptionDetailPage />} />
          <Route path="subscriptions/bundles/:id" element={<BundleDetailPage />} />
          <Route path="subscriptions/bundles/:id/edit" element={<BundleEditPage />} />
          <Route path="subscriptions/bundles/:id/subscribe" element={<BundleSubscribePage />} />
          
          {/* Tag routes */}
          <Route path="tags" element={<TagListPage />} />
          <Route path="tags/new" element={<TagCreatePage />} />
          <Route path="tags/:id" element={<TagDetailPage />} />
          <Route path="tags/:id/edit" element={<TagEditPage />} />
          <Route path="tags/settings" element={<TagSettingsPage />} />
          
          {/* Task routes — unified workspace replaces separate list/board/calendar/timeline/schedule */}
          <Route path="tasks" element={<TaskWorkspacePage />} />
          <Route path="tasks/new" element={<TaskCreatePage />} />
          <Route path="tasks/stats" element={<TaskStatsPage />} />
          <Route path="tasks/settings" element={<TaskSettingsPage />} />
          <Route path="tasks/ready" element={<TaskReadyPage />} />
          <Route path="tasks/graph" element={<TaskDependencyGraphPage />} />
          <Route path="tasks/:id" element={<TaskDetailPage />} />
          <Route path="tasks/:id/edit" element={<TaskEditPage />} />
          <Route path="task-templates" element={<TaskTemplateListPage />} />
          <Route path="task-templates/new" element={<TaskTemplateCreatePage />} />
          <Route path="task-templates/:id" element={<TaskTemplateDetailPage />} />
          <Route path="task-templates/:id/edit" element={<TaskTemplateEditPage />} />
          <Route path="settings/tasks" element={<TaskAdminPage />} />
          <Route path="settings/task-types" element={<TaskTypeListPage />} />
          <Route path="settings/task-types/new" element={<TaskTypeCreatePage />} />
          <Route path="settings/task-types/:id" element={<TaskTypeEditPage />} />
          
          {/* Team routes */}
          <Route path="teams" element={<TeamListPage />} />
          <Route path="teams/new" element={<TeamCreatePage />} />
          <Route path="teams/:id" element={<TeamWorkspacePage />} />
          <Route path="teams/:id/detail" element={<TeamDetailPage />} />
          <Route path="teams/:id/edit" element={<TeamEditPage />} />
          <Route path="teams/:id/settings" element={<TeamSettingsPage />} />
          <Route path="teams/:id/members" element={<TeamMembersPage />} />
          <Route path="teams/:id/members/:memberId" element={<TeamMemberDetailPage />} />
          <Route path="teams/:id/roles" element={<TeamRolesPage />} />
          <Route path="teams/:id/roles/:roleId" element={<TeamRoleDetailPage />} />
          <Route path="teams/:id/applications" element={<TeamApplicationsPage />} />
          <Route path="teams/:id/ownership" element={<TeamOwnershipPage />} />
          <Route path="teams/:id/audit" element={<TeamAuditLogPage />} />
          <Route path="teams/:id/branding" element={<TeamBrandingPage />} />
          <Route path="teams/:id/schedules" element={<TeamSchedulesPage />} />
          <Route path="teams/:id/schedules/:scheduleId" element={<TeamScheduleDetailPage />} />
          <Route path="teams/:id/delegations" element={<TeamDelegationsPage />} />
          <Route path="teams/:id/delegations/:delegationId" element={<TeamDelegationDetailPage />} />
          <Route path="teams/:id/temporary-roles" element={<TeamTemporaryRolesPage />} />
          <Route path="teams/:id/automation-log" element={<TeamAutomationEventsPage />} />
          <Route path="teams/:id/my-settings" element={<TeamUserSettingsPage />} />
          {/* Entity service config (deep-dive from rail) */}
          <Route path="teams/:id/services/documents" element={<EntityDocumentConfigPage entityType="team" />} />
          
          {/* Tenant routes */}
          <Route path="tenants" element={<TenantListPage />} />
          <Route path="tenants/new" element={<TenantCreatePage />} />
          <Route path="tenants/switch" element={<TenantSwitcherPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="tenants/:id/edit" element={<TenantEditPage />} />
          <Route path="tenants/:id/dashboard" element={<TenantDashboardPage />} />
          <Route path="tenants/:id/members" element={<TenantMembersPage />} />
          <Route path="tenants/:id/onboarding" element={<TenantOnboardingPage />} />
          <Route path="tenants/:id/settings" element={<TenantGeneralSettingsPage />} />
          <Route path="tenants/:id/settings/branding" element={<TenantBrandingPage />} />
          <Route path="tenants/:id/settings/security" element={<TenantSecurityPage />} />
          <Route path="tenants/:id/settings/sso" element={<TenantSSOPage />} />
          <Route path="tenants/:id/settings/quotas" element={<TenantQuotasPage />} />
          <Route path="tenants/:id/settings/export" element={<TenantDataExportPage />} />
          <Route path="tenants/:id/settings/danger" element={<TenantDangerZonePage />} />
          <Route path="tenants/settings" element={<TenantSettingsPage />} />

          {/* Admin - Tenant Management */}
          <Route path="admin/tenants" element={<AdminTenantsPage />} />
          <Route path="admin/tenants/migrations" element={<AdminMigrationsPage />} />
          <Route path="admin/tenants/:id" element={<AdminTenantDetailPage />} />
          
          {/* User routes */}
          <Route path="users" element={<UserListPage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="users/:id/edit" element={<UserEditPage />} />
          <Route path="users/settings" element={<UserSettingsPage />} />
          
          {/* Userprofile routes */}
          <Route path="userprofiles" element={<UserprofileListPage />} />
          <Route path="userprofiles/new" element={<UserprofileCreatePage />} />
          <Route path="userprofiles/:id" element={<UserprofileDetailPage />} />
          <Route path="userprofiles/:id/edit" element={<UserprofileEditPage />} />
          <Route path="userprofiles/settings" element={<UserprofileSettingsPage />} />
          {/* Userprofile admin console — directory/moderation + media repair.
              See docs/ux/userprofile-ia.md. */}
          <Route path="admin/userprofile" element={<AdminRoute><AdminUserprofilePage /></AdminRoute>} />

          {/* Webhook routes */}
          <Route path="webhooks" element={<WebhookListPage />} />
          <Route path="webhooks/:id" element={<WebhookDetailPage />} />
          <Route path="webhooks/settings" element={<WebhookSettingsPage />} />
          
          {/* Workflow routes */}
          <Route path="workflows" element={<WorkflowListPage />} />
          <Route path="workflows/new" element={<WorkflowCreatePage />} />
          <Route path="workflows/settings" element={<WorkflowSettingsPage />} />
          <Route path="workflows/approvals" element={<ApprovalsPage />} />
          <Route path="workflows/credentials" element={<CredentialListPage />} />
          <Route path="workflows/node-types" element={<NodeTypeListPage />} />
          <Route path="workflows/policies" element={<PipelinePolicyListPage />} />
          <Route path="workflows/prompt-templates" element={<PromptTemplateListPage />} />
          <Route path="workflows/prompt-templates/new" element={<PromptTemplateEditPage />} />
          <Route path="workflows/prompt-templates/:templateId" element={<PromptTemplateEditPage />} />
          <Route path="workflows/agent-tools" element={<AgentToolListPage />} />
          <Route path="workflows/agent-tools/new" element={<AgentToolEditPage />} />
          <Route path="workflows/agent-tools/:toolId" element={<AgentToolEditPage />} />
          <Route path="workflows/templates" element={<WorkflowTemplateListPage />} />
          <Route path="workflows/executions/:id" element={<ExecutionDetailPage />} />
          <Route path="workflows/:id" element={<WorkflowDetailPage />} />
          <Route path="workflows/:id/edit" element={<WorkflowEditPage />} />
          
        <Route path="*" element={<Navigate to="/inbox" replace />} />
      </Route>
    </Routes>
  );
}
